import React, { forwardRef, type ReactNode } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Button, type ButtonProps } from '../button/Button'

export type DoubleButtonProps = {
    children: ReactNode
    /** The glyph beside the label. Figma draws one; which one is the caller's. */
    icon?: ReactNode
    /** The glyph in the right segment — what makes the button a double one. */
    moreIcon: ReactNode
    onPress?: () => void
    onMorePress?: () => void
    disabled?: boolean
    accessibilityLabel?: string
    moreAccessibilityLabel?: string
    buttonProps?: Omit<ButtonProps, 'children' | 'size' | 'type' | 'onPress' | 'disabled'>
    className?: string
    testID?: string
}

type DoubleButtonRef = React.ComponentRef<typeof View>

/**
 * A primary action with a second, narrower segment beside it.
 *
 * Design rebuilt this component: it used to be a four-variant set with a right
 * half borrowed from another section, and it is now two ordinary `Button`s at
 * `size=big, type=primary` with three overrides between them. The contract
 * records exactly those, and this component applies them and nothing else.
 *
 * - Each half drops the two corners that meet the seam — an override to `null`
 *   from `common`, which is a corner switched off rather than a value left
 *   unbound.
 * - The right half narrows its horizontal padding from `medium-less-dense/m` to
 *   `small-compact/s`, because it holds a glyph and no label.
 * - The seam itself is `small-compact/3xs-seam`, a 1px token that exists for
 *   this and nothing else. The first contract generated for this component read
 *   the inner gap instead and emitted `gap-s` — eight times too wide, splitting
 *   a control whose whole point is that its halves touch.
 *
 * States come from `Button`, so each half reacts on its own. That is the change
 * design made and it fixes the old behaviour, where hovering the right half lit
 * the left one because `hover` was a variant of the whole component.
 */
export const DoubleButton = forwardRef<DoubleButtonRef, DoubleButtonProps>(
    (
        {
            children,
            icon,
            moreIcon,
            onPress,
            onMorePress,
            disabled = false,
            accessibilityLabel,
            moreAccessibilityLabel,
            buttonProps,
            className,
            testID,
        },
        ref,
    ) => (
        <View
            ref={ref}
            className={twMerge('flex-row gap-3xs-seam rounded-common', className)}
            testID={testID ?? 'double-button'}
        >
            <Button
                size='big'
                type='primary'
                iconLeft={icon}
                onPress={onPress}
                disabled={disabled}
                accessibilityLabel={accessibilityLabel}
                className='rounded-r-none'
                testID='double-button-main'
                {...buttonProps}
            >
                {children}
            </Button>
            <Button
                size='big'
                type='primary'
                iconLeft={moreIcon}
                onPress={onMorePress}
                disabled={disabled}
                accessibilityLabel={moreAccessibilityLabel}
                className='rounded-l-none px-s'
                testID='double-button-more'
                {...buttonProps}
            />
        </View>
    ),
)

DoubleButton.displayName = 'DoubleButton'
